import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Button from 'components/Button';
import PageContainer from 'components/PageContainer';
import Summary from '../Common/Summary';
import { INVESTOR_REPORTING } from 'adminConstants/routes';
import { getDeploymentUpdate } from '@redux/actions/DeploymentUpdate';
import PageContent from 'components/PageContent';
import api from 'lib/trpcClient';
import { useGetRole } from 'hooks/ui/useGetRole';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Row } from 'components/Layout';
import Spacing from 'components/Spacing';

type DeploymentUpdateViewData = {
  subject?: string;
  title?: string;
  emailTopParagraph?: string;
  emailBottomParagraph?: string;
  companyId?: {
    tradingName?: string;
    description?: string;
    companyLogo?: string;
  } | null;
  fileAttachments: Array<{
    name: string;
    path: string;
    preview: string;
  }>;
};

function isDeploymentUpdateViewData(
  value: unknown,
): value is DeploymentUpdateViewData {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !('message' in value)
  );
}

const ViewCustomUpdate: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { isSuperAdmin } = useGetRole();
  const [deploymentUpdate, setDeploymentUpdate] =
    useState<DeploymentUpdateViewData | null>(null);
  const resendEmails = api.deploymentUpdate.resendEmails.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Deployment update emails sent successfully.',
    }),
  );
  const breadcrumbs = [
    { label: 'Dashboard', link: '/' },
    {
      label: 'Investor Reporting',
      link: INVESTOR_REPORTING,
    },
  ];
  const heading = 'Investor reporting: View deployment update';
  const fetchDeploymentUpdate = async () => {
    const response = await dispatch(getDeploymentUpdate(id));
    setDeploymentUpdate(isDeploymentUpdateViewData(response) ? response : null);
  };

  useEffect(() => {
    fetchDeploymentUpdate();
  }, [id]);

  const handleResendEmails = () => {
    if (id) {
      resendEmails.mutate({ deploymentUpdateId: id });
    }
  };

  return (
    <PageContainer heading={heading} breadcrumbs={breadcrumbs}>
      <PageContent header="Deployment update summary">
        <Summary
          data={deploymentUpdate}
          company={deploymentUpdate?.companyId}
        />
        {isSuperAdmin && (
          <Spacing size="md">
            <Row justify="flex-end">
              <Button
                variant="outlined"
                onClick={handleResendEmails}
                disabled={!id || resendEmails.isPending}
                loading={resendEmails.isPending}
              >
                Resend deployment update emails
              </Button>
            </Row>
          </Spacing>
        )}
      </PageContent>
    </PageContainer>
  );
};

export default ViewCustomUpdate;
